/**
 * SAID-DIIS — Preview Banner.
 *
 * Shown at the very top of the screen when a SuperAdmin is "previewing"
 * another role's portal. Strictly visual — the auth JWT is still the
 * SuperAdmin's, so backend endpoints that `require_role([...])` on a
 * narrower role will still 403 for role-scoped data. The banner makes
 * that caveat obvious and lets the SuperAdmin exit in one click.
 */
import React from 'react';
import { Eye, X } from 'lucide-react';

const ROLE_LABEL = {
  admin: 'Faculty / Admin',
  clerk: 'Record Clerk',
  student: 'Student',
};

export default function PreviewBanner({ role, onExit }) {
  const label = ROLE_LABEL[role] || role;
  return (
    <div
      data-testid="preview-banner"
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 60,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 18px',
        background: 'linear-gradient(180deg, #FFF4E0 0%, #FBE9C7 100%)',
        borderBottom: '1px solid rgba(176, 128, 44, 0.35)',
        color: '#5B3E10',
        fontFamily: 'var(--diis-font-sans, system-ui)',
        fontSize: 13,
        lineHeight: 1.3,
      }}
    >
      <Eye size={15} style={{ flexShrink: 0, color: '#8A5A14' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>Previewing as {label}.</strong>{' '}
        <span style={{ color: '#6B4E1E' }}>
          Visual preview only — API calls still carry your SuperAdmin identity, so role-restricted data may appear empty.
        </span>
      </div>
      <button
        type="button"
        onClick={onExit}
        data-testid="preview-exit-btn"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 11px',
          fontSize: 12,
          fontWeight: 600,
          color: '#5B3E10',
          background: 'rgba(255, 255, 255, 0.65)',
          border: '1px solid rgba(176, 128, 44, 0.5)',
          borderRadius: 6,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <X size={13} /> Exit preview
      </button>
    </div>
  );
}
